import {
  CalendarOutlined,
  DownloadOutlined,
  FileOutlined,
  FileTextOutlined,
  FolderOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { DocumentType } from "../../enum/common";
import { IProjectDocuments } from "../../models/Research";
import { researchService } from "../../services/researchService";
import { formatFileSize } from "../../utils/common";
import { notify } from "../../utils/toast";
import ProjectDocumentHistoryDownload from "../research_development/ProjectDocumentHistoryDownload";

const { Title, Text } = Typography;

interface ModalViewDocumentDetailProps {
  isOpen: boolean;
  onClose: () => void;
  document: IProjectDocuments | null;
}

const ModalViewDocumentDetail = ({
  isOpen,
  onClose,
  document,
}: ModalViewDocumentDetailProps) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const getDocumentTypeLabel = (type: DocumentType) => {
    switch (type) {
      case DocumentType.DESIGN:
        return "Thiết kế";
      case DocumentType.BOM:
        return "BOM";
      case DocumentType.DOCUMENTATION:
        return "Tài liệu chung";
      case DocumentType.SPECIFICATION:
        return "Thông số kỹ thuật";
      case DocumentType.TEST_REPORT:
        return "Báo cáo thử nghiệm";
      default:
        return "Khác";
    }
  };

  const handleDownload = async () => {
    if (!document) return;
    try {
      setIsDownloading(true);
      const url = await researchService.downloadDocumentProject(document.id);
      window.open(url, "_blank");
    } catch (error: any) {
      notify.error(error?.message || "Không thể tải file");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!document) return null;

  return (
    <Modal
      title={<Title level={4}>Chi tiết tài liệu</Title>}
      open={isOpen}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose} size="large">
          Đóng
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={isDownloading}
          size="large"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
          }}
        >
          {isDownloading ? "Đang tải..." : "Tải xuống"}
        </Button>,
      ]}
    >
      <div style={{ position: "relative" }}>
        {isDownloading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(4px)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            <Card
              style={{
                textAlign: "center",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
            >
              <Space direction="vertical" size="middle">
                <DownloadOutlined
                  style={{
                    fontSize: "48px",
                    color: "#667eea",
                  }}
                />
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    Đang lấy link download...
                  </Title>
                  <Text type="secondary">Vui lòng đợi trong giây lát</Text>
                </div>
              </Space>
            </Card>
          </div>
        )}

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card
            bordered={false}
            style={{
              background:
                "linear-gradient(135deg, #667eea10 0%, #764ba210 100%)",
            }}
          >
            <Space align="start" size={20}>
              <Avatar
                size={64}
                icon={<FileOutlined />}
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              />
              <div style={{ flex: 1 }}>
                <Title level={5} style={{ marginBottom: 12 }}>
                  {document.fileName}
                </Title>
                <Space size="middle" wrap>
                  <Text type="secondary">
                    <FileTextOutlined /> {formatFileSize(document.fileSize)}
                  </Text>
                  <Text type="secondary">
                    <FolderOutlined />{" "}
                    {getDocumentTypeLabel(document.documentType)}
                  </Text>
                  <Tag color="purple">Phiên bản: {document.version}</Tag>
                </Space>
              </div>
            </Space>
          </Card>

          <Card>
            <Descriptions
              size="middle"
              bordered
              labelStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item
                span={3}
                label={
                  <Space>
                    <DownloadOutlined />
                    Số lượt tải xuống
                  </Space>
                }
              >
                {document.downloadCount}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={
                  <Space>
                    <CalendarOutlined />
                    Ngày upload
                  </Space>
                }
              >
                {new Date(document.createdAt).toLocaleDateString("vi-VI", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Descriptions.Item>
              {document.tags && document.tags.length > 0 && (
                <Descriptions.Item
                  span={3}
                  label={
                    <Space>
                      <TagsOutlined />
                      Tags
                    </Space>
                  }
                >
                  <Space wrap size={8}>
                    {document.tags.map((tag, index) => (
                      <Tag
                        key={index}
                        color={
                          ["purple", "blue", "green", "orange", "magenta"][
                            index % 5
                          ]
                        }
                      >
                        {tag}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              {document.note && (
                <Descriptions.Item
                  label={
                    <Space>
                      <FileTextOutlined />
                      Ghi chú
                    </Space>
                  }
                >
                  {document.note}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
          <ProjectDocumentHistoryDownload documentId={document.id} />
        </Space>
      </div>
    </Modal>
  );
};

export default ModalViewDocumentDetail;
